import React, { useEffect, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { Settings as SettingsIcon, FileText, Layers, Image as ImageIcon } from "lucide-react";
import { BrandLogoField } from "@/components/BrandLogoField";
import { FooterTextField } from "@/components/FooterTextField";
import { DivisionRulesEditor } from "@/components/DivisionRulesEditor";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = { fits4you: "/logo.jpg", npl: "/logo-npl.png" };

function FooterPreview({ logos, text }) {
  const src = (kind) =>
    logos?.[kind]?.path ? `${BACKEND}/api/public/files/${logos[kind].path}` : DEFAULTS[kind];
  return (
    <div className="card p-5" data-testid="footer-preview">
      <div className="overline mb-3 flex items-center gap-2">
        <FileText size={12} /> Prévia do rodapé dos PDFs
      </div>
      <div className="bg-white px-6 pt-4 pb-3">
        <div className="border-t border-[#C9A84C] pt-2 flex items-center justify-between gap-3">
          <img src={src("fits4you")} alt="Fits4You" className="h-9 object-contain" />
          <span className="text-[11px] text-[#6B6B6B] font-sans text-center flex-1" data-testid="footer-preview-text">{text}</span>
          <img src={src("npl")} alt="NPL" className="h-9 object-contain" />
          <span className="text-[8px] text-[#9A9A9A] font-sans">Página 1</span>
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("marca");

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const logos = settings?.brand_logos || {};

  return (
    <div data-testid="settings-page">
      <div className="mb-6">
        <div className="overline mb-2 flex items-center gap-2">
          <SettingsIcon size={13} /> Sistema
        </div>
        <h1 className="font-display text-5xl font-black tracking-tight">CONFIGURAÇÕES</h1>
      </div>

      {error && (
        <div className="card p-4 mb-6 text-sm text-[#FF6B6B]" data-testid="settings-error">
          {error}
        </div>
      )}

      <div className="flex gap-2 mb-6 flex-wrap" data-testid="settings-tabs">
        <button
          className={`btn-ghost !py-2 !text-xs inline-flex items-center gap-1 ${tab === "marca" ? "!bg-[#C9A84C] !text-[#0A0A0A] !border-[#C9A84C]" : ""}`}
          onClick={() => setTab("marca")}
          data-testid="settings-tab-marca"
        >
          <ImageIcon size={13} /> Marca e rodapé dos PDFs
        </button>
        <button
          className={`btn-ghost !py-2 !text-xs inline-flex items-center gap-1 ${tab === "divisoes" ? "!bg-[#C9A84C] !text-[#0A0A0A] !border-[#C9A84C]" : ""}`}
          onClick={() => setTab("divisoes")}
          data-testid="settings-tab-divisoes"
        >
          <Layers size={13} /> Divisões de Categorias
        </button>
      </div>

      {tab === "divisoes" && (
        <>
          <div className="mb-4">
            <div className="font-display text-xl font-bold tracking-tight">DIVISÕES DE CATEGORIAS</div>
          </div>
          <DivisionRulesEditor />
        </>
      )}

      {tab === "marca" && (<>
      <div className="mb-4">
        <div className="font-display text-xl font-bold tracking-tight">LOGOS INSTITUCIONAIS</div>
        <div className="text-sm text-[var(--text-secondary)] mt-1">
          Aparecem lado a lado no rodapé de todos os PDFs (roteiro, súmulas, classificação, equipes, relatório do evento).
          Envie versões em PNG com fundo transparente; sem envio, o sistema usa as logos padrão.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <BrandLogoField
          kind="fits4you"
          label="Fits4You"
          defaultSrc={DEFAULTS.fits4you}
          logo={logos.fits4you}
          onChanged={load}
        />
        <BrandLogoField kind="npl" label="NPL" defaultSrc={DEFAULTS.npl} logo={logos.npl} onChanged={load} />
      </div>

      <div className="mb-6">
        <FooterTextField
          value={settings?.footer_text}
          defaultValue={settings?.footer_text_default || "Powered by Fits4You | NPL - National Physique League"}
          onSaved={load}
        />
      </div>

      <FooterPreview logos={logos} text={settings?.footer_text || ""} />
      </>)}
    </div>
  );
}
